import { toBooleanPreprocessor } from '@lokalise/zod-extras'
import { createEnv } from '@t3-oss/env-nextjs'
import { z } from 'zod'

export const env = createEnv({
  emptyStringAsUndefined: true,
  skipValidation: !!process.env.STORYBOOK,
  /*
   * Serverside Environment variables, not available on the client.
   * Will throw if you access these variables on the client.
   */
  server: {
    NEXT_OUTPUT: z.string().optional(),
    NEXT_IMAGES_UNOPTIMIZED: z.preprocess(toBooleanPreprocessor, z.boolean()).optional(),
    NODE_ENV: z.string().optional(),
    HTTPS_REDIRECT: z.enum(['disabled', 'enabled']).optional(),
    STRAPI_REST_READONLY_API_KEY: z.string().optional(),
    STRAPI_REST_CUSTOM_API_KEY: z.string().optional(),
    STRAPI_URL: z.url(),
    APP_PUBLIC_URL: z.url(),
    DISABLE_PAGE_PREGENERATION: z.preprocess(toBooleanPreprocessor, z.boolean()).optional(),
    FAIL_BUILD_ON_CMS_FETCH_ERROR: z.preprocess(toBooleanPreprocessor, z.boolean()).optional(),
    STRAPI_PREVIEW_SECRET: z.string().optional(),
    STRAPI_PREVIEW_DEBUG: z.preprocess(toBooleanPreprocessor, z.boolean()).optional(),
    NEXT_TRAILING_SLASH: z.preprocess(toBooleanPreprocessor, z.boolean()).optional(),
  },
  /*
   * Environment variables available on the client (and server).
   * You'll get type errors if these are not prefixed with NEXT_PUBLIC_.
   */
  client: {
    NEXT_PUBLIC_APP_ENV: z.enum(['development', 'staging', 'production']),
    NEXT_PUBLIC_LOCALIZATION_ENABLED: z.preprocess(toBooleanPreprocessor, z.boolean()).optional(),
    NEXT_PUBLIC_DEV_STRAPI_URL: z.url().optional(),
    NEXT_PUBLIC_REVALIDATE: z.number().or(z.literal(false)).optional(),
    NEXT_PUBLIC_BUGSNAG_ENABLED: z.boolean().optional(),
    NEXT_PUBLIC_BUGSNAG_API_KEY: z.string().optional(),
    NEXT_PUBLIC_BUGSNAG_APP_TYPE: z.string().default('website-ui'),
    NEXT_PUBLIC_BUGSNAG_APP_VERSION: z.string().default('unknown'),
    NEXT_PUBLIC_AMPLITUDE_API_KEY: z.string().optional(),
    NEXT_PUBLIC_WEB_EXPERIMENTS_ENABLED: z.boolean().optional(),
  },
  /*
   * Due to how Next.js bundles environment variables on Edge and Client,
   * we need to manually destructure them to make sure all are included in bundle.
   * You'll get type errors if not all variables from `server` & `client` are included here.
   */
  runtimeEnv: {
    NEXT_OUTPUT: process.env.NEXT_OUTPUT,
    NEXT_IMAGES_UNOPTIMIZED: process.env.NEXT_IMAGES_UNOPTIMIZED,
    NODE_ENV: process.env.NODE_ENV,
    HTTPS_REDIRECT: process.env.HTTPS_REDIRECT,
    STRAPI_REST_READONLY_API_KEY: process.env.STRAPI_REST_READONLY_API_KEY,
    STRAPI_REST_CUSTOM_API_KEY: process.env.STRAPI_REST_CUSTOM_API_KEY,
    STRAPI_URL: process.env.STRAPI_URL,
    APP_PUBLIC_URL: process.env.APP_PUBLIC_URL,
    DISABLE_PAGE_PREGENERATION: process.env.DISABLE_PAGE_PREGENERATION === 'true',
    FAIL_BUILD_ON_CMS_FETCH_ERROR: process.env.FAIL_BUILD_ON_CMS_FETCH_ERROR === 'true',
    STRAPI_PREVIEW_SECRET: process.env.STRAPI_PREVIEW_SECRET,
    STRAPI_PREVIEW_DEBUG: process.env.STRAPI_PREVIEW_DEBUG,
    NEXT_TRAILING_SLASH: process.env.NEXT_TRAILING_SLASH,
    NEXT_PUBLIC_APP_ENV: process.env.NEXT_PUBLIC_APP_ENV,
    NEXT_PUBLIC_LOCALIZATION_ENABLED: process.env.NEXT_PUBLIC_LOCALIZATION_ENABLED,
    NEXT_PUBLIC_DEV_STRAPI_URL: process.env.NEXT_PUBLIC_DEV_STRAPI_URL,
    NEXT_PUBLIC_REVALIDATE: (() => {
      const revalidate = process.env.NEXT_PUBLIC_REVALIDATE
      const coercedRevalidate =
        revalidate != null
          ? Number.isNaN(Number(revalidate))
            ? false
            : Number(revalidate)
          : undefined

      return coercedRevalidate
    })(),
    NEXT_PUBLIC_BUGSNAG_ENABLED: process.env.NEXT_PUBLIC_BUGSNAG_ENABLED === 'true',
    NEXT_PUBLIC_BUGSNAG_API_KEY: process.env.NEXT_PUBLIC_BUGSNAG_API_KEY,
    NEXT_PUBLIC_BUGSNAG_APP_TYPE: process.env.NEXT_PUBLIC_BUGSNAG_APP_TYPE,
    NEXT_PUBLIC_BUGSNAG_APP_VERSION: process.env.NEXT_PUBLIC_BUGSNAG_APP_VERSION,
    NEXT_PUBLIC_AMPLITUDE_API_KEY: process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY,
    NEXT_PUBLIC_WEB_EXPERIMENTS_ENABLED: process.env.NEXT_PUBLIC_WEB_EXPERIMENTS_ENABLED === 'true',
  },
})
